import { useCtaTracking } from '@lib/hooks/use-cta-tracking'
import { withPublicVideoUrl } from '@lib/utils/cdn'
import { getFileExtension } from '@lib/utils/file'

import {
  SingleColBeforeBreakpoints,
  TwoColumnRatio,
  VerticalAlignment,
} from '@constants/enums/layout'
import { type IconSprites } from '@constants/enums/sprites'
import { ControlsLocation } from '@constants/enums/video'

import { updateAssetCDNUrl } from '@components/asset/utils'
import { AnimationVideo } from '@components/asset/video/animation-video/animation-video'
import { TwoColumnLayout } from '@components/layout/two-column/two-column'
import { Column } from '@components/ui-core/column/column'
import { Icon } from '@components/ui-core/icon/icon'
import { Link } from '@components/ui-core/link/link'
import { List } from '@components/ui-core/list/list'
import { Picture } from '@components/ui-core/picture/picture'
import { Text } from '@components/ui-core/text/text'
import { type Source, type VideoProps } from '@components/ui-core/video/video'

import { type SectionContent } from '@custom/pages/homepage/types'

import styles from './tabbed-section-content.module.scss'

function LeftColumnContent({ data }: { data: SectionContent }) {
  const { trackAndNavigate, isLoading } = useCtaTracking({ placement: 'Section' })

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!data?.link?.fields?.targetUrl) {
      return
    }
    trackAndNavigate(
      data?.title ?? 'TabbedSectionLink',
      data?.link?.fields?.title ?? '',
      'text',
      data?.link?.fields?.targetUrl,
      e,
    )
  }
  return (
    <>
      <div className={styles.titleWrapper}>
        {data?.icon && <Icon variant={data?.icon as IconSprites} />}
        <Text className={styles.title} as="h2" variant="headerSmall" fontWeight="regular">
          {data?.title}
        </Text>
      </div>
      <Column className={styles.content}>
        <div className={styles.headingWrapper}>
          <Text className={styles.heading} as="h2" variant="headerLarge" fontWeight="regular">
            {data?.heading}
          </Text>
        </div>
        <div className={styles.copyWrapper}>
          <Text variant="bodyMedium">{data?.copy}</Text>
        </div>
        <div className={styles.linkWrapper}>
          <Link
            className={styles.link}
            href={data?.link?.fields?.targetUrl}
            disabled={isLoading}
            onClick={handleClick}
          >
            <span className={styles.linkText}>{data?.link?.fields?.title}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="14.17"
              fill="none"
              viewBox="0 0 20 14.17"
            >
              <circle cx="1" cy="7.5" r="1" fill="currentColor" />
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.667"
                d="M13 1.667 18.833 7.5 13 13.333M4.666 7.5h14.167"
              />
            </svg>
          </Link>
        </div>
        <List className={styles.list} items={data?.list} itemClassName={styles.listItem} />
      </Column>
    </>
  )
}

function RightColumnContent({ data, index }: { data: SectionContent; index: number }) {
  const hasImage = !!data?.media?.image?.path
  const videoSources =
    data?.media?.video?.sources?.map(source => {
      const ext = getFileExtension(source) || 'mp4'
      return {
        id: source,
        url: withPublicVideoUrl(source),
        type: `video/${ext}`,
      } as Source
    }) ?? []

  const showVideo = videoSources.length > 0

  const videoOptions: VideoProps = {
    autoPlay: true,
    loop: true,
    sources: videoSources,
    controls: false,
    lazyLoad: true,
    width: data?.media?.video?.width,
    height: data?.media?.video?.height,
    poster: data?.media?.video?.poster ? updateAssetCDNUrl(data?.media?.video?.poster) : undefined,
  }

  return (
    <div className={styles.mediaWrapper}>
      {showVideo ? (
        <AnimationVideo
          className={styles.video}
          controlsLocation={ControlsLocation.InsideBottomLeft}
          videoOptions={videoOptions}
        />
      ) : hasImage ? (
        <Picture
          className={styles.image}
          fetchPriority={index === 0 ? 'high' : 'auto'}
          loading={index === 0 ? 'eager' : 'lazy'}
          src={updateAssetCDNUrl(data?.media?.image?.path ?? '')}
          height={data?.media?.image?.height}
          width={data?.media?.image?.width}
          alt={data?.media?.image?.alt}
        />
      ) : null}
    </div>
  )
}

export default function TabbedSectionContent({
  data,
  index,
}: {
  data: SectionContent
  index: number
}) {
  return (
    <TwoColumnLayout
      className={styles.container}
      columnRatio={TwoColumnRatio.OneToOne}
      singleColBefore={SingleColBeforeBreakpoints.Medium}
      leftContent={<LeftColumnContent data={data} />}
      rightContent={<RightColumnContent data={data} index={index} />}
      leftColumnVerticalAlignment={VerticalAlignment.Top}
    />
  )
}
